import { ListType } from '@/types';
import { InsertShopsLists } from '@/composables/shop/ShopsLists';
import { ShopsListsInputItem } from '@/types/generated-types/graphql';
import type {
  ImportAdapter,
  ImportContext,
  FieldDefinition,
  ImportWizardState,
  InitImportResponse,
  ConsentAllOptions,
} from '../types';
import { createAdapter, hasContactIdentifier } from './base.adapter';
import { initContactImport as initContactImportApi } from '../importService';
import type { FieldMappingDto } from '../importService';
import { fetchShopConfigurationsNest } from '@/composables/shop/ShopsConfiguration';

// =============================================================================
// FIELD DEFINITIONS
// =============================================================================

const label = (fieldName: string) => `import.contact.fields.${fieldName}.label`;
const hint = (fieldName: string) => `import.contact.fields.${fieldName}.hint`;
const group = (groupName: string) => `import.contact.groups.${groupName}`;

const CONTACT_FIELDS: FieldDefinition[] = [
  // =========================================================================
  // Identification properties (at least one required)
  // =========================================================================
  {
    name: 'email',
    type: 'text',
    required: false,
    isIdentifier: true,
    label: label('email'),
    formatHint: hint('email'),
    group: group('identification'),
  },
  {
    name: 'phone_number',
    type: 'text',
    required: false,
    isIdentifier: true,
    label: label('phone_number'),
    formatHint: hint('phone_number'),
    group: group('identification'),
  },
  {
    name: 'push_endpoint',
    type: 'text',
    required: false,
    isIdentifier: true,
    label: label('push_endpoint'),
    formatHint: hint('push_endpoint'),
    group: group('identification'),
  },
  {
    name: 'push_key',
    type: 'text',
    required: false,
    isIdentifier: true,
    label: label('push_key'),
    formatHint: hint('push_key'),
    group: group('identification'),
  },
  {
    name: 'push_token',
    type: 'text',
    required: false,
    isIdentifier: true,
    label: label('push_token'),
    formatHint: hint('push_token'),
    group: group('identification'),
  },

  // =========================================================================
  // Consent properties
  // =========================================================================
  {
    name: 'email_consent',
    type: 'text',
    required: false,
    label: label('email_consent'),
    formatHint: hint('email_consent'),
    group: group('consent'),
  },
  {
    name: 'sms_consent',
    type: 'text',
    required: false,
    label: label('sms_consent'),
    formatHint: hint('sms_consent'),
    group: group('consent'),
  },
  {
    name: 'whatsapp_consent',
    type: 'text',
    required: false,
    label: label('whatsapp_consent'),
    formatHint: hint('whatsapp_consent'),
    group: group('consent'),
  },
  {
    name: 'tracking_consent',
    type: 'text',
    required: false,
    label: label('tracking_consent'),
    formatHint: hint('tracking_consent'),
    group: group('consent'),
  },
  {
    // Affiché à l'import seulement si la boutique a activé la gestion des clics
    // (filtré dans getFields selon shopDataSubscriptionPage_showTrackingClic).
    name: 'tracking_clic_consent',
    type: 'text',
    required: false,
    label: label('tracking_clic_consent'),
    formatHint: hint('tracking_clic_consent'),
    group: group('consent'),
  },

  // =========================================================================
  // Additional properties
  // =========================================================================
  {
    name: 'first_name',
    type: 'text',
    required: false,
    label: label('first_name'),
    group: group('additional'),
  },
  {
    name: 'last_name',
    type: 'text',
    required: false,
    label: label('last_name'),
    group: group('additional'),
  },
  {
    name: 'birth_date',
    type: 'date',
    required: false,
    label: label('birth_date'),
    formatHint: hint('birth_date'),
    group: group('additional'),
  },
  {
    name: 'created_on_shop_at',
    type: 'date',
    required: false,
    label: label('created_on_shop_at'),
    formatHint: hint('created_on_shop_at'),
    group: group('additional'),
  },
  {
    name: 'contact_gender',
    type: 'text',
    required: false,
    label: label('contact_gender'),
    formatHint: hint('contact_gender'),
    group: group('additional'),
  },
  {
    name: 'language',
    type: 'text',
    required: false,
    label: label('language'),
    formatHint: hint('language'),
    group: group('additional'),
  },
  {
    name: 'is_active',
    type: 'boolean',
    required: false,
    label: label('is_active'),
    formatHint: hint('is_active'),
    group: group('additional'),
  },
  {
    name: 'address_line1',
    type: 'text',
    required: false,
    label: label('address_line1'),
    group: group('additional'),
  },
  {
    name: 'address_line2',
    type: 'text',
    required: false,
    label: label('address_line2'),
    group: group('additional'),
  },
  {
    name: 'city',
    type: 'text',
    required: false,
    label: label('city'),
    group: group('additional'),
  },
  {
    name: 'region',
    type: 'text',
    required: false,
    label: label('region'),
    group: group('additional'),
  },
  {
    name: 'country',
    type: 'text',
    required: false,
    label: label('country'),
    formatHint: hint('country'),
    group: group('additional'),
  },
  {
    name: 'postal_code',
    type: 'text',
    required: false,
    label: label('postal_code'),
    group: group('additional'),
  },
  {
    name: 'company',
    type: 'text',
    required: false,
    label: label('company'),
    group: group('additional'),
  },
];

// =============================================================================
// CONTACT IMPORT ADAPTER
// =============================================================================

/**
 * Validate contact mapping - requires at least one identifier
 */
function validateContactMapping(mapping: Record<string, string>): boolean {
  const {
    email,
    phone_number: phoneNumber,
    push_endpoint: pushEndpoint,
    push_key: pushKey,
    push_token: pushToken,
  } = mapping;

  // At least email or phone_number must be mapped
  if (email || phoneNumber) return true;

  // Or all three push fields must be mapped
  if (pushEndpoint && pushKey && pushToken) return true;

  return false;
}

/**
 * Initialize contact import
 * Uses the generic import service to call the API
 */
async function initContactImport(
  ctx: ImportContext,
  state: ImportWizardState,
  _fileType: string,
): Promise<InitImportResponse> {
  let idShopList = ctx.params?.listId as number | string | undefined;

  // If no listId provided, create a new list
  if (!idShopList && ctx.params?.listName) {
    const newShopList: ShopsListsInputItem = {
      id_shop: ctx.params?.idShop as number,
      name: ctx.params?.listName as string,
      total_contacts: 0,
      total_subscribers: 0,
      total_unsubscribeds: 0,
      total_sends: 0,
      total_sends_soft_bounce: 0,
      total_sends_hard_bounce: 0,
      total_sends_spam: 0,
      total_newsletters_viewed: 0,
      total_newsletters_clicked: 0,
      date_add: new Date(),
      date_modification: new Date(),
      active: 1,
      rating: 0,
      valid: true,
      whitelist: false,
      type: ListType.LIST,
    };

    const newList = await InsertShopsLists([newShopList]);
    idShopList = (newList && newList.status && newList.id) ? newList.id : 0;
  }

  if (!idShopList) {
    throw new Error('No list ID provided for contact import');
  }

  // Convert mapping to the expected format
  const fieldMappings: FieldMappingDto[] = state.mapping.map((m) => ({
    source: m.source,
    target: m.target,
  }));

  const requiredPushTargets = ['push_endpoint', 'push_key', 'push_token'];
  const hasCompletePushMapping = requiredPushTargets.every((target) => fieldMappings.some((m) => m.target === target));
  const vapidKeyId = hasCompletePushMapping ? state.selectedVapidKeyId : undefined;

  // Prepare consent_all options if set
  let consentAll: ConsentAllOptions | undefined;
  if (state.consentAll && (state.consentAll.email || state.consentAll.sms || state.consentAll.whatsapp)) {
    consentAll = state.consentAll;
  }

  // Call the import service
  const result = await initContactImportApi(idShopList, fieldMappings, consentAll, vapidKeyId);

  if (!result.success) {
    throw new Error(result.error);
  }

  return {
    jobId: result.data.jobId,
    uploadUrl: result.data.uploadUrl,
    uploadToken: result.data.uploadToken,
    expiresAt: result.data.expiresAt,
  };
}

/**
 * Contact Import Adapter
 */
export const contactAdapter: ImportAdapter = createAdapter({
  type: 'contact',

  steps: [
    {
      id: 'file_select',
      type: 'file_select' as const,
      descriptionKey: 'import.wizard.steps.select',
      allowCopyPaste: true,
    },
    {
      id: 'column_mapping',
      type: 'column_mapping' as const,
      descriptionKey: 'import.wizard.steps.map',
    },
    {
      id: 'preview',
      type: 'preview' as const,
      descriptionKey: 'import.wizard.steps.preview',
    },
  ],

  getFields: async (ctx) => {
    // Champ « suivi des clics » à l'import : seulement si la boutique a activé la
    // gestion des clics (même réglage que la carte clics de la page de préférences).
    const idShop = ctx.params?.idShop as number | undefined;
    let showTrackingClic = false;
    if (idShop) {
      try {
        const cfg: any = await fetchShopConfigurationsNest(idShop, [
          'shopDataSubscriptionPage_showTrackingClic',
        ]);
        showTrackingClic = cfg?.shopDataSubscriptionPage_showTrackingClic == 1;
      } catch {
        showTrackingClic = false;
      }
    }
    return showTrackingClic
      ? CONTACT_FIELDS
      : CONTACT_FIELDS.filter((f) => f.name !== 'tracking_clic_consent');
  },

  validateMapping: validateContactMapping,

  initImport: initContactImport,

  getPreviewData: (state: ImportWizardState) => ({
    totalRows: state.parsedFile?.rows?.length ?? 0,
    mappedFields: state.mapping.length,
    fileName: state.fileData.file?.name ?? 'pasted-data.csv',
  }),

  // Custom canProceed for contact import - requires at least one identifier
  canProceed: (stepIndex: number, state: ImportWizardState) => {
    const stepTypes = ['file_select', 'column_mapping', 'preview'];
    const stepType = stepTypes[stepIndex];

    switch (stepType) {
      case 'file_select':
        if (state.fileData.useCopyPaste) {
          return state.fileData.pastedText.trim().length > 0;
        }
        return state.fileData.file !== null;

      case 'column_mapping':
        // Must have at least one identifier mapped
        return hasContactIdentifier(state);

      case 'preview':
        return true;

      default:
        return false;
    }
  },
});

export default contactAdapter;
